import { Button, Header, Stack, StackItem } from '@asgard/ui-library';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FormCheckbox } from 'components/form/FormCheckbox';
import { FormPhone } from 'components/form/FormPhone';
import { FormSelect } from 'components/form/FormSelect';
import { FormText } from 'components/form/FormText/FormText';
import { FormTextarea } from 'components/form/FormTextarea';
import { PageContentContainer } from 'components/layout/Page.styled';
import HtmlTrans from 'components/shared/HtmlTrans';
import { LinkText } from 'components/shared/LinkText';
import { PHONE_NUMBER_DEFAULT_COUNTRY } from 'features/portal/v2/contact/basic-info/ContactBasicInfo.constants';
import { Trans } from 'next-i18next';
import { FormProvider } from 'react-hook-form';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { useContactForm } from './ContactForm.hooks';
import { Form } from './ContactForm.styled';
import { getTypeOptions } from './ContactForm.utils';

const ContactForm: React.FC = () => {
  const {
    t,
    formMethods,
    locale,
    onSubmit,
    isTermsAccepted,
    onFormFieldFocus
  } = useContactForm();

  const {
    handleSubmit,
    formState: { isSubmitting }
  } = formMethods;

  return (
    <Form.Root>
      <PageContentContainer containerSize="xlMax">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Form.Intro>
            <Header editorial as="h1" size="5xl">
              {t('contact.form.title')}
            </Header>
            <HtmlTrans i18nKey={'contact.form.desc'} ns="contact" />

            <HtmlTrans i18nKey={'contact.form.disclaimer'} ns="contact" />
          </Form.Intro>

          <FormProvider {...formMethods}>
            <Stack direction="column" gap="3xl">
              <Stack>
                <StackItem className="half">
                  <FormText
                    name="name"
                    formGroup={{
                      label: `${t('contact.form.name')} *`
                    }}
                    input={{
                      placeholder: t('contact.form.name_placeholder'),
                      onFocus: onFormFieldFocus
                    }}
                  />
                </StackItem>

                <StackItem className="half">
                  <FormText
                    name="company"
                    formGroup={{
                      label: `${t('contact.form.company')} *`
                    }}
                    input={{
                      placeholder: t('contact.form.company_placeholder'),
                      onFocus: onFormFieldFocus
                    }}
                  />
                </StackItem>
              </Stack>

              <Stack>
                <StackItem className="half">
                  <FormText
                    name="email"
                    formGroup={{
                      label: `${t('contact.form.email')} *`
                    }}
                    input={{
                      placeholder: t('contact.form.email_placeholder'),
                      onFocus: onFormFieldFocus
                    }}
                  />
                </StackItem>
                <StackItem className="half phoneNumber">
                  <FormPhone
                    name="contactPhone"
                    formGroup={{
                      label: t('contact.form.phone')
                    }}
                    input={{
                      defaultCountry: PHONE_NUMBER_DEFAULT_COUNTRY,
                      onFocus: onFormFieldFocus
                    }}
                  />
                </StackItem>
              </Stack>

              <FormSelect
                name="howDoYouKnowUs"
                formGroup={{
                  label: `${t('contact.form.how-do-you-know-us')} *`
                }}
                options={getTypeOptions(t)}
              />

              <FormTextarea
                name="description"
                formGroup={{
                  label: `${t('contact.form.description')} *`
                }}
                input={{
                  placeholder: t('contact.form.description_placeholder'),
                  onFocus: onFormFieldFocus,
                  rows: 3
                }}
              />

              <Stack direction="column" gap="s">
                <FormCheckbox
                  name="terms"
                  input={{
                    onFocus: onFormFieldFocus,
                    label: (
                      <Trans
                        ns={'contact'}
                        i18nKey={'contact.form.terms'}
                        components={{
                          privatePolicy: (
                            <LinkText
                              href={getAppUrl(APP_URLS.privacyPolicy, locale)}
                            />
                          ),
                          terms: (
                            <LinkText
                              href={getAppUrl(APP_URLS.termsOfUse, locale)}
                            />
                          )
                        }}
                      />
                    )
                  }}
                />

                <FormCheckbox
                  name="isSubscribeToNewsletter"
                  input={{
                    onFocus: onFormFieldFocus,
                    label: (
                      <Trans
                        ns={'contact'}
                        i18nKey={'contact.form.newsletter'}
                        components={{
                          newsletter: (
                            <a
                              href="https://none.clickacm.com/show/qgJ2lZXPBBizIfJi3pmS/"
                              target="_blank"
                            />
                          )
                        }}
                      ></Trans>
                    )
                  }}
                />
              </Stack>

              <StackItem>
                <Button
                  schema="brand1"
                  variant="hard"
                  label={t('contact.form.submit')}
                  startIcon={faCheckCircle}
                  type="submit"
                  disabled={!isTermsAccepted}
                  isLoading={isSubmitting}
                  loadingText={t('contact.form.submit_in-progress')}
                />
              </StackItem>
            </Stack>
          </FormProvider>
        </form>
      </PageContentContainer>
    </Form.Root>
  );
};

export default ContactForm;
